import {
  activityRows,
  commitments,
  FUNNEL_DAYS,
  lei,
  localDay,
  moneyByCategory,
  moneyBySite,
  siteLabel,
  timeAgo,
  type Funnel as FunnelData,
  type Savings as SavingsData,
  type SiteMoney,
  type SupplierScore,
  t,
  intlLocale,
} from '@procumi/shared';
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { CommitMonths } from '../components/CommitMonths';
import { Empty, Panel } from '../components/Panel';
import { ErrorBox, Loading } from '../components/StateBox';
import { useAsync } from '../hooks/useAsync';
import { api } from '../lib/api';
import type { Route } from '../routes';

/**
 * Analiza: banii, lunile, categoriile, șantierele, parcursul cererilor și
 * furnizorii. Stătea pe panou; din 2026-10-10 panoul e lista de lucru a
 * achizitorului („Azi”), iar cifrele de privit în timp stau aici.
 */
export function AnalysisPage({ go }: { go: (r: Route) => void }) {
  const sumar = useAsync(() => api.dashboard(), [], 'dashboard');
  const scoruri = useAsync(() => api.scorecard(), [], 'scorecard');
  const [santier, setSantier] = useState('');
  const acum = useMemo(() => Date.now(), [sumar.data]);
  const today = localDay(acum);
  const d = sumar.data;

  const peSantier = <T extends { site: { id: string } }>(rows: T[]) =>
    santier ? rows.filter((r) => r.site.id === santier) : rows;
  const orders = useMemo(() => peSantier(d?.orders ?? []), [d, santier]);
  const rfqs = useMemo(() => peSantier(d?.rfqs ?? []), [d, santier]);
  const serii = useMemo(() => commitments(orders, rfqs, acum), [orders, rfqs, acum]);
  const peBani = useMemo(() => moneyBySite(orders, d?.budgets ?? {}, today), [orders, d, today]);
  const peCategorii = useMemo(() => moneyByCategory(orders), [orders]);
  const activitate = useMemo(() => activityRows(d?.events ?? [], FEED_PAGES * FEED_PER_PAGE), [d]);
  const santiere = useMemo(() => {
    const m = new Map<string, { id: string; name: string; locality: string; address: string }>();
    for (const r of d?.rfqs ?? []) m.set(r.site.id, r.site);
    for (const o of d?.orders ?? []) m.set(o.site.id, o.site);
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name, 'ro'));
  }, [d]);

  if (sumar.loading) return <Loading />;
  if (sumar.error || !d) {
    return <ErrorBox error={sumar.error ?? t('Nu am putut încărca.')} onRetry={sumar.reload} />;
  }

  return (
    <>
      <div className="head">
        <div>
          <h1>{t('Analiză')}</h1>
          <p className="head-facts">{t('Banii, lunile și furnizorii, din comenzile și cererile tale')}</p>
        </div>
        {santiere.length > 1 ? (
          <select
            className="inp dash-site"
            aria-label={t('Șantier')}
            value={santier}
            onChange={(e) => setSantier(e.target.value)}
          >
            <option value="">{t('Toate șantierele')}</option>
            {santiere.map((s) => (
              <option key={s.id} value={s.id}>
                {siteLabel(s)}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      <div className="an-grid">
        <Panel
          className="an-wide"
          title={t('Achiziții și angajamente')}
          hint={
            <span className="legend">
              <span className="key spend">{t('achiziționat')}</span>
              <span className="key commit">{t('angajat')}</span>
              <span className="key plan">{t('planificat')}</span>
            </span>
          }
        >
          <CommitMonths series={serii} currentKey={today.slice(0, 7)} />
        </Panel>

        <Panel title={t('Diferență obținută')} hint={t('ultimele 12 luni')}>
          <Savings s={d.savings} />
        </Panel>

        <Panel title={t('Pe categorii')} hint={t('comandat')}>
          {peCategorii.length ? (
            <BarList
              rows={peCategorii.map((c) => ({
                key: c.category,
                label: c.category,
                value: c.total,
                sub: `${c.orders} ${c.orders === 1 ? t('comandă') : t('comenzi')}`,
                onClick: () => go({ view: 'rfqs', q: c.category, state: 'closed' }),
              }))}
            />
          ) : (
            <Empty>{t('Apare după prima comandă emisă.')}</Empty>
          )}
        </Panel>

        <Panel title={t('Pe șantier')}>
          {peBani.length ? (
            <SiteMoneyTable rows={peBani} onPick={(id) => go({ view: 'site', id })} />
          ) : (
            <Empty>{t('Apare după prima comandă emisă.')}</Empty>
          )}
        </Panel>

        <Panel
          className="an-wide"
          title={t('Parcursul cererilor')}
          hint={t('ultimele {0} de zile', FUNNEL_DAYS)}
        >
          <Funnel f={d.funnel} />
        </Panel>

        <Panel
          title={t('Furnizori')}
          action={
            <button className="link" onClick={() => go({ view: 'suppliers' })}>
              {t('Toți furnizorii')}
            </button>
          }
        >
          {scoruri.loading ? (
            <Loading />
          ) : scoruri.error ? (
            <ErrorBox error={scoruri.error} onRetry={scoruri.reload} />
          ) : (
            <Suppliers rows={scoruri.data ?? []} />
          )}
        </Panel>

        <Panel title={t('Activitate recentă')}>
          {activitate.length ? (
            <FeedPager
              items={activitate.map((e) => (
                <li key={e.key}>
                  <span className="when">{timeAgo(e.at, acum)}</span>
                  {e.rfqId ? (
                    <button
                      className="what link-plain"
                      onClick={() =>
                        e.orderId
                          ? go({ view: 'order', rfqId: e.rfqId!, orderId: e.orderId })
                          : go({ view: 'rfq', id: e.rfqId! })
                      }
                    >
                      {t(e.text)}
                    </button>
                  ) : (
                    <span className="what">{t(e.text)}</span>
                  )}
                  {e.actor ? <span className="who">{e.actor}</span> : null}
                </li>
              ))}
            />
          ) : (
            <Empty>{t('Nimic încă. Aici apar cererile, ofertele, comenzile și livrările.')}</Empty>
          )}
        </Panel>
      </div>
    </>
  );
}

/** Activitatea, pe pagini de câte 6, ca panoul să nu se lungească în jos. */
const FEED_PER_PAGE = 6;
/** Câte pagini: serverul trimite ultimele 40 de evenimente. */
const FEED_PAGES = 6;

/**
 * Paginile stau una lângă alta și se derulează lateral (cu degetul sau cu
 * trackpad-ul); punctele de jos arată pagina și duc la ea.
 */
function FeedPager({ items }: { items: ReactNode[] }) {
  const track = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);
  const pages: ReactNode[][] = [];
  for (let i = 0; i < items.length; i += FEED_PER_PAGE) pages.push(items.slice(i, i + FEED_PER_PAGE));
  const goTo = (i: number) => {
    const el = track.current;
    if (!el) return;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollTo({ left: i * el.clientWidth, behavior: still ? 'auto' : 'smooth' });
    setPage(i);
  };
  return (
    <div className="feed-pager">
      <div
        className="feed-track"
        ref={track}
        onScroll={(e) => {
          const el = e.currentTarget;
          setPage(Math.round(el.scrollLeft / Math.max(1, el.clientWidth)));
        }}
      >
        {pages.map((p, i) => (
          <ol
            key={i}
            className="dash-feed feed-page"
            aria-label={t('Pagina {0} din {1}', i + 1, pages.length)}
          >
            {p}
          </ol>
        ))}
      </div>
      {pages.length > 1 ? (
        <div className="feed-dots" role="group" aria-label={t('Paginile activității')}>
          {pages.map((_, i) => (
            <button
              key={i}
              type="button"
              className={i === page ? 'on' : undefined}
              aria-label={t('Pagina {0}', i + 1)}
              aria-current={i === page ? 'true' : undefined}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Diferența obținută, cu explicația ei la vedere: nu e o „economie” umflată. */
function Savings({ s }: { s: SavingsData }) {
  if (!s.count && !s.aboveCount) {
    return (
      <Empty>
        {t(
          'Nu există încă suficiente date. Apare la comenzile unde ai ales din cel puțin două oferte complete.',
        )}
      </Empty>
    );
  }
  return (
    <div className="dash-savings">
      <b className="big">{s.count ? lei(s.gained) : '—'}</b>
      <p className="muted">
        {s.count
          ? t(
              'Diferența dintre oferta aleasă și următoarea ofertă completă, la {0} {1}. Nu față de cea mai scumpă: un preț umflat n-ar fi o economie.',
              s.count,
              s.count === 1 ? t('comandă') : t('comenzi'),
            )
          : t('Nicio comandă la care să fi ales cea mai ieftină din cel puțin două oferte complete.')}
      </p>
      {s.aboveCount ? (
        <p className="over">
          {t(
            'La {0} {1} ai ales peste cea mai ieftină: +{2}.',
            s.aboveCount,
            s.aboveCount === 1 ? t('comandă') : t('comenzi'),
            lei(s.above),
          )}
        </p>
      ) : null}
      {s.supplementsLei ? (
        <p className="muted">
          {t('Suplimentări fără concurență: {0}, nu intră aici.', lei(s.supplementsLei))}
        </p>
      ) : null}
    </div>
  );
}

/** Bare orizontale simple: eticheta, bara, suma. */
function BarList({
  rows,
}: {
  rows: { key: string; label: string; value: number; sub?: string; onClick?: () => void }[];
}) {
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <div className="bars">
      {rows.slice(0, 8).map((r) => (
        <button className="bar-row" key={r.key} onClick={r.onClick} disabled={!r.onClick}>
          <span className="bar-label">
            {r.label}
            {r.sub ? <span className="s">{r.sub}</span> : null}
          </span>
          <span className="bar-track">
            <span className="bar-fill" style={{ width: `${(r.value / max) * 100}%` }} />
          </span>
          <span className="bar-val">{lei(r.value)}</span>
        </button>
      ))}
    </div>
  );
}

/** Pe șantier: achiziționat, angajat și, unde e trecut, bugetul. Fără coloană de buget inventată. */
function SiteMoneyTable({ rows, onPick }: { rows: SiteMoney[]; onPick: (id: string) => void }) {
  const anyBudget = rows.some((r) => r.budget != null);
  return (
    <div className="dash-table-wrap">
      <table className="dash-table">
        <thead>
          <tr>
            <th>{t('Șantier')}</th>
            <th className="n">{t('Achiziționat')}</th>
            <th className="n">{t('Angajat')}</th>
            {anyBudget ? <th className="n">{t('Buget')}</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 8).map((r) => {
            const used = r.budget ? (r.bought + r.committed) / r.budget : null;
            return (
              <tr key={r.site.id} className="pick" onClick={() => onPick(r.site.id)}>
                <td>
                  <span className="t">{r.site.name}</span>
                </td>
                <td className="n">{lei(r.bought)}</td>
                <td className="n">{r.committed ? lei(r.committed) : '—'}</td>
                {anyBudget ? (
                  <td className="n">
                    {r.budget != null ? (
                      <>
                        {lei(r.budget)}
                        <span className={`s${used != null && used > 1 ? ' over' : ''}`}>
                          {t('{0}% folosit', Math.round((used ?? 0) * 100))}
                        </span>
                      </>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
      {!anyBudget ? (
        <p className="hint" style={{ padding: '0 20px 16px', margin: 0 }}>
          {t('Trece bugetul pe șantier (Șantiere → Editează) ca să vezi cât din el e folosit.')}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Parcursul cererilor, ca un traseu: un cerc cu numărul pe fiecare etapă și,
 * între ele, cât la sută trec mai departe. Culoarea spune etapa, în culorile
 * mărcii: ultra în lucru, night la decizie și comandă, gri la livrat și finalizat
 * (verdele e doar al economiilor).
 */
function Funnel({ f }: { f: FunnelData }) {
  if (!f.created) return <Empty>{t('Nicio cerere în ultimele {0} de zile.', FUNNEL_DAYS)}</Empty>;
  const steps: { label: string; n: number; tone: string; icon: string }[] = [
    {
      label: t('Cereri'),
      n: f.created,
      tone: 'blue',
      icon: 'M9.2 1.8H4.8A1.6 1.6 0 0 0 3.2 3.4v9.2a1.6 1.6 0 0 0 1.6 1.6h6.4a1.6 1.6 0 0 0 1.6-1.6V5.4L9.2 1.8ZM9 2v3.2h3.6M5.8 8.6h4.4M5.8 11h2.8',
    },
    { label: t('Trimise'), n: f.sent, tone: 'blue', icon: 'M14 2 7 9M14 2l-4.5 12-2.5-5-5-2.5z' },
    {
      label: t('Cu oferte'),
      n: f.offers,
      tone: 'blue',
      icon: 'M2 9.5 3.6 3.8A1.2 1.2 0 0 1 4.8 3h6.4a1.2 1.2 0 0 1 1.2.8L14 9.5V12a1.2 1.2 0 0 1-1.2 1.2H3.2A1.2 1.2 0 0 1 2 12zM2 9.5h3.2l.8 1.6h4l.8-1.6H14',
    },
    {
      label: t('Decise'),
      n: f.chosen,
      tone: 'night',
      icon: 'M8 2v12M4.4 14h7.2M3 4.4h10M3 4.4l-1.8 4.2a2 2 0 0 0 3.6 0L3 4.4ZM13 4.4l-1.8 4.2a2 2 0 0 0 3.6 0L13 4.4Z',
    },
    {
      label: t('Comandă'),
      n: f.ordered,
      tone: 'night',
      icon: 'M3.2 2.6h9.6v10.8H3.2zM5.6 5.4h4.8M5.6 7.8h4.8M6 10.8l1.2 1.2 2.6-2.6',
    },
    {
      label: t('Livrare'),
      n: f.delivered,
      tone: 'muted',
      icon: 'M1.5 4h8v7h-8zM9.5 6.5h3l2 2.5v2h-5M4.5 13a1.3 1.3 0 1 0 0-2.6 1.3 1.3 0 0 0 0 2.6zM11.5 13a1.3 1.3 0 1 0 0-2.6 1.3 1.3 0 0 0 0 2.6z',
    },
    { label: t('Finalizată'), n: f.completed, tone: 'muted', icon: 'm3.5 8.5 3 3 6-7' },
  ];
  return (
    <ol className="pipe">
      {steps.map((st, i) => {
        const prev = i ? steps[i - 1]!.n : 0;
        const pct = i && prev ? Math.round((st.n / prev) * 100) : null;
        return (
          <li key={st.label} className={`pipe-step ${st.tone}${st.n ? '' : ' zero'}`}>
            {i ? (
              <span className="pipe-arrow" aria-hidden="true">
                <svg width="28" height="10" viewBox="0 0 28 10">
                  <path
                    d="M1 5h24M21 1.5 25 5l-4 3.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <small>{pct == null ? '—' : `${pct}%`}</small>
              </span>
            ) : null}
            <span className="pipe-node">
              <span className="pipe-ring">
                <b>{st.n}</b>
              </span>
              <span className="pipe-label">
                <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true">
                  <path
                    d={st.icon}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {st.label}
              </span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/** Furnizorii cu cele mai multe cereri: cât răspund și cât de repede. */
function Suppliers({ rows }: { rows: SupplierScore[] }) {
  const used = rows.filter((r) => r.sent > 0).sort((a, b) => b.sent - a.sent || b.chosen - a.chosen);
  if (!used.length) {
    return (
      <Empty>{t('Apare după primele cereri trimise: cine răspunde, cât de repede, pe cine alegi.')}</Empty>
    );
  }
  return (
    <div className="dash-table-wrap">
      <table className="dash-table">
        <thead>
          <tr>
            <th>{t('Furnizor')}</th>
            <th className="n">{t('Răspunde')}</th>
            <th className="n">{t('În medie')}</th>
            <th className="n">{t('Ales')}</th>
          </tr>
        </thead>
        <tbody>
          {used.slice(0, 6).map((s) => (
            <tr key={s.supplier.id}>
              <td>
                <span className="t">{s.supplier.name}</span>
                <span className="s">
                  {s.sent} {s.sent === 1 ? t('cerere') : t('cereri')}
                </span>
              </td>
              <td className="n">{Math.round(s.responseRate * 100)}%</td>
              <td className="n">
                {s.avgDays == null
                  ? '—'
                  : `${s.avgDays.toLocaleString(intlLocale())} ${s.avgDays === 1 ? t('zi') : t('zile')}`}
              </td>
              <td className="n">{s.chosen}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
